import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import ScoreCard from '../../components/ScoreCard';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import { adminApi } from '../../services/admin';

export default function AdminGuardDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: guard, isLoading } = useQuery({
    queryKey: ['admin', 'guards', id],
    queryFn: () => adminApi.getGuard(id!),
    enabled: !!id,
  });

  if (isLoading) return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>;
  if (!guard) return <div className="text-center py-12"><p className="text-sm text-slate-500">Guard not found</p></div>;

  return (
    <>
      <PageHeader
        title={guard.name}
        description={guard.external_id ? `ID: ${guard.external_id}` : undefined}
        actions={
          <button onClick={() => navigate('/admin/guards')} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50">
            Back to Guards
          </button>
        }
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Guard Information</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">First Name</dt>
              <dd className="mt-1 text-sm text-slate-900">{guard.first_name}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Last Name</dt>
              <dd className="mt-1 text-sm text-slate-900">{guard.last_name}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Email</dt>
              <dd className="mt-1 text-sm text-slate-900">{guard.email || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Phone</dt>
              <dd className="mt-1 text-sm text-slate-900">{guard.phone || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Status</dt>
              <dd className="mt-1">
                <StatusBadge label={guard.is_active ? 'Active' : 'Inactive'} variant={guard.is_active ? 'success' : 'neutral'} />
              </dd>
            </div>
          </dl>
        </div>
        <ScoreCard label="Guard Score" score={guard.score} size="lg" />
      </div>

      {/* Assigned Sites */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          Assigned Sites ({guard.assigned_sites?.length ?? 0})
        </h2>
        {guard.assigned_sites && guard.assigned_sites.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Site Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Assigned</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {guard.assigned_sites.map((s) => (
                  <tr key={s.id} className="cursor-pointer hover:bg-blue-50/50" onClick={() => navigate(`/admin/sites/${s.id}`)}>
                    <td className="px-6 py-4 text-sm text-slate-900 font-medium">{s.name}</td>
                    <td className="px-6 py-4 text-sm text-slate-500">{s.city}, {s.state}</td>
                    <td className="px-6 py-4 text-sm text-slate-500">{new Date(s.assigned_date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400">No sites assigned.</p>
        )}
      </div>
    </>
  );
}
